<template>
  <v-card data-testid="detail-form">
    <v-dialog
    width="700px"
    v-model="autoPopulateDialog">
      <AutoPopulateDialog
      v-if="autoPopulateDialog"
      :contract="contract"
      :activity="activity"
      :tractContents="tractContents"
      @cancel="autoPopulateDialog = false"
      @populated="populateSuccess"
      />
    </v-dialog>

    <v-dialog
    v-model="confirmDetailCost"
    persistent
    width="400">
      <DetailCostWarning v-if="confirmDetailCost"
      :activityCost="activity.baseCost.rate"
      :detailCost="detail.cost.rate"
      :contract="contract"
      @cancel="confirmDetailCost = false"
      @create-detail="handleDetailWarningResult"
      />
    </v-dialog>

    <v-card-title :class="contractTypeColor" v-if="!hideTitle">
      <span class="headline">{{ title }}</span>

      <v-spacer/>

      <Icon
      v-if="allowAutopopulate"
      iconColor="white"
      icon="mdi-animation"
      dataTestId="detail-populate-button"
      :small="false"
      :tooltipText="$t('autoPopulateButtonText')"
      @icon-clicked="autoPopulateDialog = true"
      />

      <Icon
      iconColor="white"
      icon="mdi-close"
      dataTestId="detail-close"
      :small="false"
      :large="true"
      :tooltipText="$t('close')"
      @icon-clicked="$emit('cancel')"
      />
    </v-card-title>

    <v-card-text class="mt-4 pb-4">
      <FormWrapper
        :buttonText="saveButtonText"
        :buttonColor="contractTypeColor"
        @submit="postActivityDetail"
        :disabled="disableSaveButton"
      >
        <v-container fluid grid-list-md>
          <v-row wrap>
            <v-col cols="12" sm="6">
              <LoadableComponent :loading="!productInitialized">
                <v-autocomplete
                ref="firstField"
                color="black"
                item-color="primary"
                v-model="product"
                :label="$t('product')"
                return-object
                :items="availableProducts"
                item-text="name"
                />
              </LoadableComponent>
            </v-col>

            <v-col cols="12" sm="6">
              <LoadableComponent :loading="!baseCostInitialized">
                  <BaseCostForm
                  v-if="baseCostInitialized"
                  data-testid="detail-rate"
                  :propBaseCost="detail.cost"
                  :focus.sync="costError"
                  :editing="true"
                  :hideCostCalculation="true"
                  :expectedValue="expectedRateForProduct"
                  @base-cost-changed="baseCostChanged"
                />
              </LoadableComponent>
            </v-col>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { MAX_ACTIVITY_RATE } from '@/utils/rules'
import { colorClassForContractType } from '@/utils/componentHelpers.js'
import { PayBy } from '@/utils/Enumerations'
import { uniqueDialogId } from '@/utils/componentHelpers'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'DetailForm',

  components: {
    BaseCostForm: () => import('@/components/activity/BaseCostForm.vue'),
    AutoPopulateDialog: () => import('@/components/activity/activity-detail/AutoDetailPopulate.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DetailCostWarning: () => import('@/components/activity/activity-detail/DetailCostWarning.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue')
  },

  props: {
    activity: Object,
    contractActivities: {
      type: Array,
      required: true
    },
    contract: Object,
    editing: Boolean,
    propDetail: Object,
    hideTitle: { Boolean, default: false },
    rateStats: { Object, default: undefined }
  },

  data: () => ({
    PayBy,
    loading: true,
    dialogId: uniqueDialogId('detail-form-dialog'),
    autoPopulateDialog: false,
    confirmDetailCost: false,
    costError: false,
    productError: false,
    excludedProducts: [],
    tractContents: [],
    product: {},
    detail: {
      cost: {},
      product: {}
    },
    baseCostInitialized: false,
    productInitialized: false
  }),

  computed: {
    ...mapGetters('product', ['allProducts']),

    allowAutopopulate () {
      const hasNoDetails = !this.activity.activityDetails || this.activity?.activityDetails?.length === 0
      const hasTractContents = this.tractContents.length > 0
      return hasNoDetails && hasTractContents && !this.isPayByVolumeContract
    },

    saveButtonText () {
      return this.editing ? this.$t('saveChanges') : this.$t('addDetail')
    },

    title () {
      return this.editing ? this.$t('editingDetail') : this.$t('newDetail')
    },

    expectedRateForProduct () {
      return this.rateStats?.detailStats?.find(s => s?.productId === this.product?.productId)?.recentAverage
    },

    contractTypeColor () {
      return colorClassForContractType(this.contract?.type)
    },

    isPayByVolume () {
      return this.activity?.baseCost?.payBy === PayBy.Volume.value
    },

    isPayByVolumeContract () {
      return this.contractActivities?.some(ca => ca.baseCost?.payBy === PayBy.Volume.value)
    },

    disableSaveButton () {
      return !this.product
    },

    activityDetailId () {
      return this.editing ? this.propDetail?.activityDetailId : undefined
    },

    availableProducts () {
      return this.contract.products.filter(p => !this.excludedProducts.includes(p.name) || (this.editing && p.productId === this.product?.productId))
    }
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.firstField.focus()
    }, 500)
  },

  async beforeMount () {
    this.excludedProducts = this.activity.activityDetails?.map(d => d.product.name) ?? []
  },

  async created () {
    await this.refreshTractContents(this.contract.tractId)
    if (this.editing) {
      this.detail = cloneDeep(this.propDetail)
      this.baseCostInitialized = true
      this.product = this.detail.product
      this.productInitialized = true
    } else {
      this.detail.cost = cloneDeep(this.activity.baseCost)
      this.baseCostInitialized = true
      this.productInitialized = true
    }
    this.loading = false
  },

  methods: {
    ...mapActions('activity-detail', [
      'createActivityDetail',
      'updateActivityDetail'
    ]),
    ...mapActions('activity', ['updateActivity']),
    ...mapActions('tract-contents', ['fetchTractContents']),

    baseCostChanged (baseCost) {
      this.detail.cost = baseCost
    },

    async postActivityDetail () {
      if (!this.validate()) {
        return
      }
      this.$emit('detail-mutated', { ...this.detail, product: this.product })
    },

    async refreshTractContents () {
      if (this.contract.tractId) {
        const tractContents = await this.fetchTractContents({
          tractId: this.contract.tractId
        })
        this.tractContents = tractContents
      }
    },

    async handleDetailWarningResult () {
      this.$emit('handle-detail-cost-warning', { ...this.detail, product: this.product })
    },

    validate () {
      if (!this.product.name) {
        this.setSnackError(this.$t('noProductSelected'))
        return false
      }

      if (this.activity.baseCost.rate > this.detail.cost.rate) {
        this.confirmDetailCost = true
        return false
      }

      if (MAX_ACTIVITY_RATE < this.detail.cost.rate) {
        this.setSnackError(this.$t('invalidActivityRate'))
        return false
      }

      return true
    },

    populateSuccess () {
      this.$emit('detail-added')
      this.autoPopulateDialog = false
    }
  }
}
</script>
